/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  Input,
  EventEmitter,
  Output,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { XdaStringsService } from '../../services/xda-strings.service';
import { UserService } from '../../services/user.service';
import { XrxControlBaseComponent } from '../controls/xrx-control-base.component';
import { SettingType } from '../../../common/enums';
import { SummarizationMethodHelpComponent } from './modals/summarization-method-help.component';
import { OptionListOptions, Parameter } from '../../services/workflow.service';

@Component({
  selector: 'xrx-summarization',
  templateUrl: './xrx-summarization.component.html',
  styleUrls: ['./xrx-summarization.component.less'],
  providers: [
    {
      provide: XrxControlBaseComponent,
      useExisting: XrxSummarizationComponent,
    },
  ],
  standalone: false,
})
export class XrxSummarizationComponent
  extends XrxControlBaseComponent
  implements OnInit, OnDestroy
{
  @Input() required: boolean;
  @Input() label: string;
  @Input() value: string; // the summarization configuration
  @Input() list: OptionListOptions[] = []; // language list
  @Input() isAutomated = false;
  @Input() serviceIndex: number; // the service setting page, single workflow = 0, combo workflow >= 0
  @Input() setting: Parameter;

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() change = new EventEmitter<string>();
  @Output() validation = new EventEmitter<boolean>();

  languageOption: OptionListOptions;

  summarizationLength = {
    name: 'summarizationLength',
    label: 'SDE_SUMMARIZATION_LENGTH',
    icon: '',
    selectedValue: { key: 'SDE_MEDIUM', value: 'medium', icon: '' },
    type: SettingType.Select,
    options: [
      { key: 'SDE_SHORT', value: 'short', icon: '' },
      { key: 'SDE_MEDIUM', value: 'medium', icon: '', isDefault: true },
      { key: 'SDE_LONG', value: 'long', icon: '' },
    ],
  };

  config: SummarizationConfig = {
    isAbstractive: true,
    summaryLength: 'medium',
    language: 'en',
  };
  isValid = true;
  isSavedConfig: boolean;

  constructor(
    private http: HttpClient,
    private modalService: NgbModal,
    private xdaStringsService: XdaStringsService,
    private userService: UserService,
  ) {
    super();
  }

  ngOnInit() {
    this.loadSavedConfiguration();
    this.validate();
  }

  ngOnDestroy() {
    this.updateConfig();
  }

  validate() {
    this.isValid = true;
    this.setting.error = ''; // clear any previous error message
    this.validation.emit(this.isValid);
  }

  onLanguageSelected() {
    this.updateConfig();
  }

  onSummarizationLengthSelected() {
    this.updateConfig();
  }

  onNumberOfSentenceSelected() {
    this.updateConfig();
  }

  showHelp(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    this.modalService.open(SummarizationMethodHelpComponent, {
      windowClass: 'xrx-modal modal-md',
    });
  }

  loadSavedConfiguration() {
    this.config = JSON.parse(this.value);
    this.summarizationLength.selectedValue =
      this.summarizationLength.options.find(
        (x) => x.value == this.config.summaryLength,
      )!;
    // if no saved language, use user's preferred language as default language
    const language =
      this.config.language == ''
        ? this.userService.user?.language.substring(0, 2)
        : this.config.language;

    // default to English if language not found in the languages list
    this.languageOption =
      this.list.find((x) => x.value === language) ??
      this.list.find((x) => x.value === 'en')!; // default to English if language not supported

    this.updateConfig();
  }

  updateConfig() {
    this.config.language = this.languageOption.value as string;
    this.config.summaryLength = this.summarizationLength.selectedValue.value;
    this.value = JSON.stringify(this.config);
    this.change.emit(this.value);
  }
}
export interface SummarizationConfig {
  isAbstractive: boolean;
  summaryLength: string;
  language: string;
}
